// Pistol target geometry for the Megalink shots page (ISSF ring tables) and the
// grouping of stored shots into series. Pure, no DOM. Shot x/y are tenths of a
// millimetre from the target centre (megalinkShot.x / .y).
// Spec: docs/superpowers/specs/2026-10-13-megalink-tv-shots-design.md §4.1.

export const TARGETS = {
    precision: {
        tenDiameter: 50, ringStep: 50, lowestRing: 1, blackFrom: 7, innerTen: 25,
    },
    duel: {
        tenDiameter: 100, ringStep: 80, lowestRing: 5, blackFrom: 5, innerTen: 50,
    },
    air: {
        tenDiameter: 11.5, ringStep: 16, lowestRing: 1, blackFrom: 7, innerTen: 5,
    },
};

export const BULLET_MM = { luftpistol: 4.5, grovpistol: 9, default: 5.6 };

const PRECISION_THEN_DUEL = ['finpistol', 'grovpistol'];

export function bulletMm(stageType) {
    const type = typeof stageType === 'string' ? stageType.trim().toLowerCase() : '';
    return BULLET_MM[type] || BULLET_MM.default;
}

export function targetFor(stageType, seriesIndex = 0) {
    const type = typeof stageType === 'string' ? stageType.trim().toLowerCase() : '';
    if (type === 'luftpistol') return 'air';
    if (type === 'standard') return 'precision';
    if (PRECISION_THEN_DUEL.includes(type)) return seriesIndex < 6 ? 'precision' : 'duel';
    return 'duel';
}

export function ringDiameter(targetName, value) {
    const t = TARGETS[targetName] || TARGETS.duel;
    return t.tenDiameter + (10 - value) * t.ringStep;
}

export function outerDiameter(targetName) {
    const t = TARGETS[targetName] || TARGETS.duel;
    return ringDiameter(targetName, t.lowestRing);
}

export function ringValue(targetName, xTenths, yTenths) {
    const t = TARGETS[targetName] || TARGETS.duel;
    const distanceMm = Math.sqrt((xTenths / 10) ** 2 + (yTenths / 10) ** 2);
    for (let value = 10; value >= t.lowestRing; value -= 1) {
        if (distanceMm <= ringDiameter(targetName, value) / 2) return value;
    }
    return 0;
}

// Stage types whose shots the Megalink pages can draw (mirrors LAYOUTS in
// backend/lib/megalink-stage-layout.js — keep the two in step).
export const MEGALINK_STAGE_TYPES = [
    'nais', 'hurtig', 'hurtig-half', 'standard', 'finpistol', 'grovpistol', 'silhuett', 'luftpistol',
    'griseskyting',
];

export function isMegalinkStageType(stageType) {
    const type = typeof stageType === 'string' ? stageType.trim().toLowerCase() : '';
    return MEGALINK_STAGE_TYPES.includes(type);
}

// Every Lag whose program the live shots page can show, whatever its state.
// Shows the «watch live» button on the convention page before and during the
// stevne (the page itself says «no Lag running» and counts down to the next).
export function megalinkStages(convention) {
    const stages = convention && Array.isArray(convention.stages) ? convention.stages : [];
    return stages
        .map((stage, idx) => ({ stage, idx }))
        .filter(({ stage }) => !!stage && isMegalinkStageType(stage.type));
}

// The Lag that are Pågår right now (neither Ferdig nor Venter — the kanban's
// rule, see isStageOpen in backend/lib/megalink-assign.js) and whose program the
// live shots page can show. Names the running Lag on the «watch live» button.
export function runningMegalinkStages(convention) {
    return megalinkStages(convention)
        .filter(({ stage }) => parseInt(stage.stageDone, 10) !== 1
            && parseInt(stage.stageClosed, 10) !== 1);
}

// Auto zoom: the ring one below the worst shot drawn, so a group of 9s and 10s
// fills the face down to the 8-ring. null = show the whole face (no positioned
// shot, a shot outside the rings, or the zoom would reach the lowest ring anyway).
export function zoomRing(targetName, shots) {
    const t = TARGETS[targetName] || TARGETS.duel;
    const list = (Array.isArray(shots) ? shots : []).filter(s => s && Number.isFinite(s.x) && Number.isFinite(s.y));
    if (list.length === 0) return null;
    let worst = 10;
    for (let i = 0; i < list.length; i += 1) {
        const value = ringValue(targetName, list[i].x, list[i].y);
        if (value === 0) return null;
        if (value < worst) worst = value;
    }
    const ring = worst - 1;
    return ring > t.lowestRing ? ring : null;
}

export function groupShots(shots, layout) {
    const list = Array.isArray(shots) ? shots : [];
    const targets = layout && layout.targets > 0 ? layout.targets : 0;
    const series = Array.from({ length: targets }, () => []);
    const sighters = [];
    const extras = [];
    const replaced = []; // a series re-shot after a malfunction (omskyting): kept, never scored
    list.forEach(shot => {
        if (!shot) return;
        if (shot.countStatus === 'sighter') {
            sighters.push(shot);
            return;
        }
        if (shot.countStatus === 'extra') {
            extras.push(shot);
            return;
        }
        if (shot.countStatus === 'replaced') {
            replaced.push(shot);
            return;
        }
        const k = Number.isInteger(shot.cellTargetIndex) ? shot.cellTargetIndex : -1;
        if (k >= 0 && k < targets) series[k].push(shot);
    });
    let currentSeries = -1;
    series.forEach((s, k) => { if (s.length > 0) currentSeries = k; });
    return {
        sighters, series, extras, replaced, currentSeries,
    };
}

// Mean point of impact of the shots with a position (tenths of a millimetre),
// like the monitor's «Middel». null without any positioned shot.
export function meanPoint(shots) {
    const list = (Array.isArray(shots) ? shots : []).filter(s => s && Number.isFinite(s.x) && Number.isFinite(s.y));
    if (list.length === 0) return null;
    return {
        x: list.reduce((acc, s) => acc + s.x, 0) / list.length,
        y: list.reduce((acc, s) => acc + s.y, 0) / list.length,
        count: list.length,
    };
}

export function seriesTotal(shots) {
    return (Array.isArray(shots) ? shots : []).reduce((acc, s) => ({
        sum: acc.sum + (Number.isFinite(s && s.value) ? s.value : 0),
        inner: acc.inner + (s && s.innerTen ? 1 : 0),
    }), { sum: 0, inner: 0 });
}

// Megalink stores hundredths (747 → 7.47); the monitor shows the truncated
// tenth (7.4), never rounded. The epsilon keeps 10.55 * 10 = 105.49999 at 105.
export function formatShotValue(shot) {
    if (!shot) return '';
    const number = Number.isFinite(shot.decimalValue)
        ? (Math.floor(shot.decimalValue * 10 + 1e-6) / 10).toFixed(1)
        : String(shot.value ?? '');
    return `${shot.innerTen ? '*' : ''}${number}`;
}

// Spec §4.1 self-check: the ring computed from the stored position must equal
// the stored value. Only counted shots with a position and a series are checked;
// a mismatch count > 0 on a live Lag means the unit or target mapping is off.
export function countRingMismatches(shots, stageType) {
    let checked = 0;
    let mismatches = 0;
    (Array.isArray(shots) ? shots : []).forEach(shot => {
        if (!shot || shot.countStatus !== 'counted' || !Number.isInteger(shot.cellTargetIndex)) return;
        if (!Number.isFinite(shot.x) || !Number.isFinite(shot.y) || !Number.isFinite(shot.value)) return;
        checked += 1;
        if (ringValue(targetFor(stageType, shot.cellTargetIndex), shot.x, shot.y) !== shot.value) mismatches += 1;
    });
    return { checked, mismatches };
}
